import React from 'react';

export type StripeStatus = {
  connectedAccountId: string | null;
  onboardingStatus: string | null;
  detailsSubmitted: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  requirements: Record<string, any> | null;
  feeBps: number | null;
} | null;

const CheckCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="9" stroke="#16a34a" strokeWidth="2" />
    <path d="M6 10.5L8.5 13L14 7" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const WarningIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M10 2L18.66 17H1.34L10 2Z" stroke="#d97706" strokeWidth="1.5" fill="#fef3c7" strokeLinejoin="round" />
    <path d="M10 8V11M10 13.5V14" stroke="#d97706" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const LinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M6 1H2.5C1.67 1 1 1.67 1 2.5V11.5C1 12.33 1.67 13 2.5 13H11.5C12.33 13 13 12.33 13 11.5V8M8 1H13M13 1V6M13 1L6 8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const StripeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect width="20" height="20" rx="4" fill="#635bff" />
    <path d="M9.3 7.7c0-.6.5-.9 1.3-.9.9 0 2 .3 2.9.8V5.2c-1-.4-2-.5-2.9-.5-2.4 0-3.9 1.2-3.9 3.3 0 3.2 4.4 2.7 4.4 4.1 0 .7-.6 1-1.4 1-1 0-2.3-.5-3.3-1.1v2.5c1.1.5 2.3.7 3.3.7 2.4 0 4-1.2 4-3.3C13.7 8.5 9.3 9.1 9.3 7.7Z" fill="white" />
  </svg>
);

type ConnectPanelProps = {
  stripeStatus: StripeStatus;
  stripeLoading: boolean;
  onboardingLoading: boolean;
  onStartOnboarding: () => void;
};

export default function ConnectPanel({
  stripeStatus,
  stripeLoading,
  onboardingLoading,
  onStartOnboarding,
}: ConnectPanelProps) {
  const isComplete = stripeStatus?.chargesEnabled && stripeStatus?.payoutsEnabled;
  const isPending = stripeStatus?.connectedAccountId && !isComplete;
  const pendingRequirements: string[] = stripeStatus?.requirements?.currently_due || [];
  const commissionPct = stripeStatus?.feeBps != null
    ? (stripeStatus.feeBps / 100).toFixed(0)
    : null;

  if (stripeLoading) {
    return (
      <div className="card" style={{ padding: '32px' }}>
        <div className="loading" style={{ minHeight: '80px' }}><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '20px 24px', flexWrap: 'wrap', gap: '12px',
        borderBottom: '1px solid var(--gray-200)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <StripeIcon />
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--gray-900)' }}>
              Stripe Connect
            </h3>
          </div>
        </div>

        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          padding: '6px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: 600,
          background: isComplete ? '#dcfce7' : isPending ? '#fef3c7' : 'var(--gray-100)',
          color: isComplete ? '#15803d' : isPending ? '#92400e' : 'var(--gray-500)',
          border: `1px solid ${isComplete ? '#bbf7d0' : isPending ? '#fde68a' : 'var(--gray-200)'}`,
        }}>
          <span style={{
            width: '8px', height: '8px', borderRadius: '50%', display: 'inline-block',
            background: isComplete ? '#22c55e' : isPending ? '#f59e0b' : 'var(--gray-400)',
            boxShadow: isComplete ? '0 0 0 3px rgba(34,197,94,0.2)' : isPending ? '0 0 0 3px rgba(245,158,11,0.2)' : 'none',
          }} />
          {isComplete ? 'Connected' : isPending ? 'Setup Incomplete' : 'Not Connected'}
        </div>
      </div>

      <div style={{ padding: '24px' }}>
        {isComplete && (
          <>
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: '12px',
              padding: '16px 20px', background: 'var(--success-light)', borderRadius: '10px',
              border: '1px solid #bbf7d0',
            }}>
              <CheckCircleIcon />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#15803d', marginBottom: '4px' }}>
                  Payments are active
                </div>
                <div style={{ fontSize: '13px', color: '#166534', lineHeight: '1.5' }}>
                  Customer payments go directly to your Stripe account on each booking. Stripe handles the deposit to your bank on its standard payout schedule.
                </div>
              </div>
            </div>

            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: '24px',
              marginTop: '20px', padding: '16px 20px',
              background: 'var(--gray-50)', borderRadius: '10px',
            }}>
              <div>
                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                  Account ID
                </div>
                <code style={{ fontSize: '13px', color: 'var(--gray-700)', background: 'var(--gray-200)', padding: '2px 8px', borderRadius: '4px' }}>
                  {stripeStatus?.connectedAccountId}
                </code>
              </div>
              <div>
                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                  Charges
                </div>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#15803d' }}>Enabled</span>
              </div>
              <div>
                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                  Bank Payouts
                </div>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#15803d' }}>Enabled</span>
              </div>
              {commissionPct !== null && (
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                    Commission
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: commissionPct === '0' ? '#15803d' : 'var(--gray-900)' }}>
                      {commissionPct}%
                    </span>
                    {commissionPct === '0' && (
                      <span className="badge badge-success" style={{ fontSize: '11px', padding: '2px 8px' }}>
                        Partner rate
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {isPending && (
          <>
            {pendingRequirements.length > 0 && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: '12px',
                padding: '16px 20px', background: '#fffbeb', borderRadius: '10px',
                border: '1px solid #fde68a', marginBottom: '20px',
              }}>
                <WarningIcon />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#92400e', marginBottom: '8px' }}>
                    Complete your Stripe setup
                  </div>
                  <div style={{ fontSize: '13px', color: '#78350f', marginBottom: '8px' }}>
                    Stripe needs a few more details before you can accept payments:
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#78350f', lineHeight: '1.7' }}>
                    {pendingRequirements.map((r: string) => (
                      <li key={r}>{r.replace(/_/g, ' ').replace(/\./g, ' > ')}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <button
              className="btn btn-primary"
              onClick={onStartOnboarding}
              disabled={onboardingLoading}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                opacity: onboardingLoading ? 0.7 : 1,
                cursor: onboardingLoading ? 'wait' : 'pointer',
              }}
            >
              {onboardingLoading ? 'Redirecting...' : 'Continue Stripe Setup'}
              <LinkIcon />
            </button>
          </>
        )}

        {!isComplete && !isPending && (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%',
              background: 'var(--gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <StripeIcon />
            </div>
            <h4 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 600, color: 'var(--gray-900)' }}>
              Start accepting payments
            </h4>
            <p style={{ margin: '0 0 20px', fontSize: '14px', color: 'var(--gray-500)', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto', lineHeight: '1.5' }}>
              Connect your Stripe account to receive payments directly from customers. Funds are deposited to your account on each booking.
            </p>
            <button
              className="btn btn-primary"
              onClick={onStartOnboarding}
              disabled={onboardingLoading}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                opacity: onboardingLoading ? 0.7 : 1,
                cursor: onboardingLoading ? 'wait' : 'pointer',
              }}
            >
              <StripeIcon />
              {onboardingLoading ? 'Redirecting...' : 'Connect with Stripe'}
            </button>
            <p style={{ margin: '12px 0 0', fontSize: '12px', color: 'var(--gray-400)' }}>
              You'll be redirected to Stripe to verify your identity and add your bank account.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
